'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTextSelection } from './use-text-selection';

/**
 * Wraps `useTextSelection` with persistence logic so the selected text
 * remains available after the user clicks into the assistant UI (which
 * clears the browser selection). The selection is only cleared when
 * focus moves to an element _outside_ `[data-persistent-assistant]`.
 */
export function usePersistedSelection() {
    const { selection } = useTextSelection();
    const liveSelectedText = selection.text || null;

    const [persistedSelection, setPersistedSelection] = useState<string | null>(null);
    const prevLiveRef = useRef(liveSelectedText);
    const lastPointerInsideAssistantRef = useRef(false);

    useEffect(() => {
        const handlePointerDown = (e: PointerEvent) => {
            lastPointerInsideAssistantRef.current = !!(e.target as Element)?.closest?.(
                '[data-persistent-assistant]',
            );
        };

        window.addEventListener('pointerdown', handlePointerDown, true);
        return () => window.removeEventListener('pointerdown', handlePointerDown, true);
    }, []);

    useEffect(() => {
        if (liveSelectedText === prevLiveRef.current) return;
        prevLiveRef.current = liveSelectedText;

        if (liveSelectedText) {
            setPersistedSelection(liveSelectedText);
        } else if (persistedSelection) {
            if (!lastPointerInsideAssistantRef.current) {
                setPersistedSelection(null);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveSelectedText]);

    const selectedText = liveSelectedText || persistedSelection;

    const clearSelection = useCallback(() => {
        setPersistedSelection(null);
        window.getSelection()?.removeAllRanges();
    }, []);

    return { selectedText, clearSelection };
}
